/// <amd-module name="Release/Landing/HubContent/Releases/ActiveReleasesTable" />
import * as React from "react";

import { Tooltip } from "VSSUI/TooltipEx";
import { IMenuItem } from "VSSUI/Menu";

import { VssComponent, IVssComponentProperties, IVssComponentContext } from "VSS/Platform/Layout";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { format, localeFormat } from "VSS/Core/Util/String";
import { ITableColumn, Table, ColumnMore, SimpleTableCell, ITable, TableColumnStyle } from "VSSUI/Table";
import { ITableRow } from "VSSUI/Components/Table/Table.Props";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { ObservableValue, ObservableLike, IObservableArray, ObservableArray } from "VSS/Core/Observable";

import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";

import { IActiveReleasesState, ActiveReleasesStore } from "./ActiveReleasesStore";
import { ActiveReleasesActions } from "./ActiveReleasesActions";
import { ActiveReleasesMenuCommands } from "./ActiveReleasesMenuCommands";
import { Release, ReleaseStatus, Artifact } from "Release/Client/Contracts/Release";
import { IReleasesFilterState } from "Release/Landing/HubContent/Filter/FilterTypes";
import { ActiveReleaseColumnKeys, ReleasesViewCanvasConstants } from "Release/Landing/Constants";
import { createUrlForRoute, handleEventAndNavigate } from "Release/Helpers/Link";
import * as Resources from "Release/Landing/Resources";
import { NameColumn } from "./NameColumn";
import { EnvironmentsColumn, IActiveReleaseColumnProps } from "./EnvironmentsColumn";
import { getPrimaryArtifact, getArtifactBuildInfo, getPrimaryArtifactBranchName, getBranchDisplayValue } from "Release/Landing/Artifact";

export interface IActiveReleasesTableProps extends IVssComponentProperties {
    releases: Release[];
    definitionId: number;
    envColumnWidth: number;
    releaseColumnWidth: number;
    envNodeWidth: number;
    initialEnvColumnWidth: number;
    initialNameColumnWidth: number;
    initialArtifactNameWidth: number;
    initialBranchNameWidth: number;
    definitionEnvironmentCurrentReleaseMap: { [key: number]: number };
    filterState: IReleasesFilterState;
    onDesiredReleaseFound?: () => void;
    showDescriptionColumn: boolean;
    showTagsColumn: boolean;
    showDefinitionNameColumn: boolean;
}

export interface IActiveReleasesDetailsListViewState extends IActiveReleasesState {
    focusedRowReleaseId?: number;
    hoveredRowReleaseId?: number;
}

export class ActiveReleasesTable extends VssComponent<IActiveReleasesTableProps, IActiveReleasesDetailsListViewState>{
    constructor(props: IActiveReleasesTableProps, context: IVssComponentContext) {
        super(props, context);

        this._activeReleasesActions = ActionsHubManager.GetActionsHub<ActiveReleasesActions>(ActiveReleasesActions);
        this._activeReleasesStore = StoreManager.GetStore<ActiveReleasesStore>(ActiveReleasesStore, "active-releases-store", context.pageContext);
        this._menuCommands = new ActiveReleasesMenuCommands();

        this.state = this._activeReleasesStore.getState();
    }

    public componentDidUpdate() {
        this._isEnvironmentColumnResizing = false;
        this._isNameColumnResizing = false;
    }

    public render(): JSX.Element {
        return (
            <Table
                ref={(table: Table<Release>) => { this._activeReleasesTable = table!; }}
                className={"active-releases-details-list"}
                itemProvider={new ArrayItemProvider<Release>(this.props.releases)}
                columns={this._getColumns()}
                pageSize={30}
                showHeader={true}
                onActivate={this._onItemInvoked}
                showLines={false}
                singleClickActivation={false} />
        );
    }

    private _getColumns(): ITableColumn<Release>[] {
        let columns = new Array<ITableColumn<Release>>();
        let minEnvColumnWidth: number = Math.max(this.props.initialEnvColumnWidth, 150);
        let maxEnvColumnWidth: number = this.props.initialEnvColumnWidth + 12; //12px to account for margins on either side
        if (this.props.envColumnWidth > 0) {
            //On resize, the action raised sets the envColWidth in ActiveReleasesStore; Whenever RD in left panel is changed use the value previously set on resize 
            maxEnvColumnWidth = this.props.envColumnWidth;
            if (!this._isEnvironmentColumnResizing) {
                //Do not fix width if column is currently resizing 
                minEnvColumnWidth = this.props.envColumnWidth;
            }
        }

        //Setting minWidth for Name column based on release class to handle spacing between name and env columns
        const minReleaseNameColumnWidth = this.props.releaseColumnWidth > 0 ? this.props.releaseColumnWidth : this.props.initialNameColumnWidth;

        columns.push({
            id: ActiveReleaseColumnKeys.ActiveReleaseNameKey,
            name: Resources.ReleasesText,
            columnStyle: TableColumnStyle.Secondary,
            width: new ObservableValue(this.props.releaseColumnWidth > 0 ? this.props.releaseColumnWidth : minReleaseNameColumnWidth),
            headerClassName: "active-releases-details-list-header active-release-column-header",
            renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release) => this._renderReleaseNameCell(rowIndex, columnIndex, tableColumn, release),
            onSize: (event: MouseEvent, index: number, width: number) => this._onColumnResize(event, index, width)
        });

        const menuItemColumn = new ColumnMore<Release>((release: Release) => {
            return {
                id: "environments-list-context-menu",
                items: this._getMenuItems(release)
            };
        });
        columns.push(menuItemColumn);

        if (this.props.showDefinitionNameColumn) {
            columns.push({
                id: ActiveReleaseColumnKeys.ActiveReleaseDefinitionNameKey,
                name: Resources.ActiveReleaseDefinitionNameHeaderText,
                columnStyle: TableColumnStyle.Secondary,
                minWidth: 150,
                width: new ObservableValue(200),
                headerClassName: "active-releases-details-list-header",
                renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release) => this._renderReleaseDefinitionNameCell(rowIndex, columnIndex, tableColumn, release)
            });
        }

        columns.push({
            id: ActiveReleaseColumnKeys.ActiveReleaseCreatedKey,
            name: Resources.ActiveReleaseCreatedHeaderText,
            columnStyle: TableColumnStyle.Secondary,
            width: 180,
            headerClassName: "active-releases-details-list-header",
            renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release) => this._renderReleaseCreatedCell(rowIndex, columnIndex, tableColumn, release)
        });

        columns.push({
            id: ActiveReleaseColumnKeys.ActiveReleaseEnvironmentsKey,
            name: Resources.ActiveReleaseEnvironmentsHeaderText,
            columnStyle: TableColumnStyle.Secondary,
            minWidth: !this._isEnvironmentColumnResizing ? minEnvColumnWidth : 150,
            width: new ObservableValue(maxEnvColumnWidth),
            headerClassName: "active-releases-details-list-header",
            className: "active-release-environments-column",
            onSize: (event: MouseEvent, index: number, width: number) => this._onColumnResize(event, index, width),
            renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release) => this._renderEnvironmentCell(rowIndex, columnIndex, tableColumn, release)
        });

        if (this.props.showDescriptionColumn) {
            columns.push({
                id: ActiveReleaseColumnKeys.ActiveReleaseDescriptionKey,
                name: Resources.ActiveReleaseDescriptionHeaderText,
                columnStyle: TableColumnStyle.Secondary,
                minWidth: 250,
                width: new ObservableValue(this.props.showTagsColumn ? 250 : -100), // If tags column is present, take 400px, else take the entire space
                headerClassName: "active-releases-details-list-header",
                renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release) => this._renderReleaseDescriptionCell(rowIndex, columnIndex, tableColumn, release)
            });
        }

        if (this.props.showTagsColumn) {
            columns.push({
                id: ActiveReleaseColumnKeys.ActiveReleaseTagsKey,
                name: Resources.ActiveReleaseTagsHeaderText,
                columnStyle: TableColumnStyle.Secondary,
                minWidth: 250,
                width: new ObservableValue(-100), // Last column here. Take the entire remaining space
                headerClassName: "active-releases-details-list-header",
                renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release) => this._renderReleaseTagsCell(rowIndex, columnIndex, tableColumn, release)
            });
        }

        this._columns = columns;
        return columns;
    }

    private _renderReleaseNameCell(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release): JSX.Element {
        const filterState = this.props.filterState;
        const relColumnWidth: number = this.props.releaseColumnWidth > 0 ? this.props.releaseColumnWidth + 24 : ObservableLike.getValue(tableColumn.width); // 24px is for 12px padding on either side
        // Compute max width for artifact and branch after subtracting identity image (48px) and branch-artifact icons widths(16px each) from releaseColumnWidth and 12px padding on both sides
        let artifactNameMaxWidth: number = this.props.initialArtifactNameWidth;
        let branchMaxWidth: number = this.props.initialBranchNameWidth;

        // The branch and artifact widths in props are calculated as an aggregate based on all the rows in the page
        // However, there may be cases where the artifact name is large, and branch is relatively small, compared to the aggregate.
        // Recalculate the widths, but ensure that we do not overflow the sum of the aggregate build and aggregate branch. That is the total available width
        const primaryArtifact: Artifact | undefined = getPrimaryArtifact(release.artifacts);
        const artifactNameLength: number = getArtifactBuildInfo(primaryArtifact).length;
        const branchName: string = getPrimaryArtifactBranchName(release.artifacts);
        const branchDisplayNameLength: number = getBranchDisplayValue(branchName).length;
        let currentArtifactNameWidth = artifactNameLength > 0 ? (artifactNameLength * 10) + 14 : 0; // +14 for artifact icon
        let currentBranchNameWidth = branchDisplayNameLength > 0 ? (branchDisplayNameLength * 10) + 14 : 0; // +14 for the branch icon
        const currentTotalWidth = currentArtifactNameWidth + currentBranchNameWidth;
        const overallTotalWidth = artifactNameMaxWidth + branchMaxWidth;

        if (overallTotalWidth < currentTotalWidth) {
            // adjust the current widths to fit available space
            currentArtifactNameWidth = (currentArtifactNameWidth * overallTotalWidth) / currentTotalWidth;
            currentBranchNameWidth = (currentBranchNameWidth * overallTotalWidth) / currentTotalWidth;
        }

        // 104 = 48 px for the image, 24px for margins, 32px for something (update when understood)
        if (relColumnWidth < overallTotalWidth + 104) {
            // We'll do the computation below to give optimum space for both
            // If artifact takes less than half the space, give it full space, and branch will take the remainder of the column
            // If branch takes less than half the space, do vice versa
            // If both need more than half the space, give them half the space each
            const halfAvailableSize = (relColumnWidth - 104) / 2;
            if (currentArtifactNameWidth >= halfAvailableSize) {
                if (currentBranchNameWidth >= halfAvailableSize) {
                    currentArtifactNameWidth = halfAvailableSize;
                    currentBranchNameWidth = halfAvailableSize;
                }
                else {
                    currentArtifactNameWidth = relColumnWidth - currentBranchNameWidth - 104;
                }
            }
            else {
                currentBranchNameWidth = relColumnWidth - currentArtifactNameWidth - 104;
            }
        }

        const nameColProps = {
            release: release,
            releaseClass: "active-releases-name-column",
            columnWidth: relColumnWidth,
            artifactNameMaxWidth: currentArtifactNameWidth,
            branchNameMaxWidth: currentBranchNameWidth,
            focusedRowReleaseId: this.state.focusedRowReleaseId,
            hoveredRowReleaseId: this.state.hoveredRowReleaseId,
            isDeleted: filterState.isDeleted,
            onDesiredReleaseFound: this.props.onDesiredReleaseFound
        };
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <NameColumn {...nameColProps} />
            </SimpleTableCell>);
    }

    private _renderEnvironmentCell(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release): JSX.Element {
        let columnWidth: number;
        if (this.props.envColumnWidth > 0) {
            //On Env chain expand;
            //On resize
            // Also when RD in left panel changes, take the previously set width from props
            columnWidth = this.props.envColumnWidth;
        }
        else {
            columnWidth = ObservableLike.getValue(tableColumn.width);
        }

        const envColProps: IActiveReleaseColumnProps = {
            release: release,
            definitionId: this.props.definitionId,
            definitionEnvironmentCurrentReleaseMap: this.props.definitionEnvironmentCurrentReleaseMap,
            instanceId: format(this._instanceIdFormat, release.id),
            envNodeWidth: this.props.envNodeWidth,
            visibleEnvCount: this._getVisibleEnvCount(columnWidth, release),
            onDesiredReleaseFound: this.props.onDesiredReleaseFound,
            onExpandClick: (newWidth: number) => this._activeReleasesActions.onEnvironmentColumnResize.invoke({ definitionId: this.props.definitionId, newWidth: newWidth })
        };

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <EnvironmentsColumn
                    {...envColProps} />
            </SimpleTableCell>);
    }

    private _renderReleaseCreatedCell(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release): JSX.Element {
        const releaseCreatedDate = release.createdOn;
        const dateTimeString = releaseCreatedDate.toLocaleString()

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="active-release-created-details">
                    <Tooltip text={dateTimeString} overflowOnly={true}>
                        <div className="active-release-created-date">
                            {dateTimeString}
                        </div>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    }

    private _renderReleaseDescriptionCell(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release): JSX.Element {
        const releaseDescription: string = release.description;
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="active-release-tags-description-container">
                    {
                        releaseDescription && (
                            <div className="active-rel-description-container">
                                <Tooltip text={releaseDescription} overflowOnly={true} showOnFocus={true}>
                                    <span className="active-rel-description-tooltip overflow-text-container"> {releaseDescription} </span>
                                </Tooltip>
                            </div>
                        )
                    }
                </div>
            </SimpleTableCell>
        );
    }

    private _renderReleaseDefinitionNameCell(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release): JSX.Element {
        const releaseDefinitionName: string = release.releaseDefinition.name;
        const queryParams: { [key: string]: string } = {
            "definitionId": release.releaseDefinition.id.toString(10),
            "_a": "environments-editor-preview"
        };
        const editorUrl = createUrlForRoute(this.context.pageContext, "ms.vss-releaseManagement-web.cd-workflow-hub-default-route", queryParams);

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="active-rel-definition-name-container flex-row flex-center full-size">
                    <Tooltip text={releaseDefinitionName} overflowOnly={true} showOnFocus={true}>
                        <FPSLink rel="noopener noreferrer" href={editorUrl} className={"active-rel-definition-tooltip overflow-text-container"}>{releaseDefinitionName}</FPSLink>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    }

    private _renderReleaseTagsCell(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release): JSX.Element {
        const getReleaseTagElement = (tag: string, index: number): JSX.Element => {
            return (<div className="active-rel-tag-container" key={index}>
                <Tooltip text={tag} overflowOnly={true} showOnFocus={true}>
                    <span className="active-rel-tag-tooltip overflow-text-container" >
                        {tag}
                    </span>
                </Tooltip>
            </div>);
        }
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="active-release-tags-description-container">
                    <div className="active-rel-tags-container flex"> {(release.tags || []).map((tag: string, index: number) => { return getReleaseTagElement(tag, index); })} </div>
                </div>
            </SimpleTableCell>
        );
    }

    private _getMenuItems = (release: Release): IObservableArray<IMenuItem> => {
        const filterState: IReleasesFilterState = this.props.filterState;
        const menuItems: IObservableArray<IMenuItem> = new ObservableArray<IMenuItem>(this._menuCommands.getCommands(this.context.pageContext, release, filterState.isDeleted));

        const extensionContext = {
            release: release
        };
        const contributedMenuItems = getMenuItems(this.context.pageContext,
            "ms.vss-releaseManagement-web.releases-list-context-menu",
            extensionContext);

        contributedMenuItems.value.forEach(menuItem => menuItems.push(menuItem));

        contributedMenuItems.subscribe((update) => {
            if (update.addedItems) {
                update.addedItems.forEach(menuItem => menuItems.push(menuItem));
            }
        });

        return menuItems;
    }

    private _onColumnResize = (event: MouseEvent, index: number, width: number): void => {
        if (this._columns.length > 0 && this._columns[index].id === ActiveReleaseColumnKeys.ActiveReleaseEnvironmentsKey) {
            this._isEnvironmentColumnResizing = true;
            this._activeReleasesActions.onEnvironmentColumnResize.invoke({
                definitionId: this.props.definitionId,
                newWidth: width
            });
        }
        else if (this._columns.length > 0 && this._columns[index].id === ActiveReleaseColumnKeys.ActiveReleaseNameKey) {
            this._isNameColumnResizing = true;
            this._activeReleasesActions.onReleaseColumnResize.invoke({
                definitionId: this.props.definitionId,
                newWidth: width
            });
        }
    }

    private _onItemInvoked = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<Release>): void => {
        if (this.props.onDesiredReleaseFound) {
            this.props.onDesiredReleaseFound();
        }

        if (!this.state.releases || !this.state.releases[tableRow.index]) {
            return;
        }

        const release = this.state.releases[tableRow.index];
        if (release.status === ReleaseStatus.Draft) {
            const queryParams: { [key: string]: string } = {
                "releaseId": release.id.toString(10),
                "_a": "release-environments-editor"
            };
            const draftUrl = createUrlForRoute(this.context.pageContext, "ms.vss-releaseManagement-web.draft-release-route", queryParams);
            FastPageSwitch(this.context.pageContext, draftUrl, true);
        }
        else if (!this.props.filterState.isDeleted) {
            const releaseUrl = createUrlForRoute(this.context.pageContext, "ms.vss-releaseManagement-web.cd-release-progress-default-route", {
                "_a": "release-pipeline-progress",
                "releaseId": release.id.toString(10)
            });
            handleEventAndNavigate(this.context.pageContext, event, releaseUrl);
        }
    }

    private _getVisibleEnvCount(columnWidth: number, releaseItem: Release): number {
        const actualEnvCount: number = releaseItem.environments.length;

        if (this.props.envNodeWidth === ReleasesViewCanvasConstants.EnvironmentNodeWidthSmall) {
            return Math.floor((columnWidth - 33) / (ReleasesViewCanvasConstants.EnvironmentNodeWidthSmall + ReleasesViewCanvasConstants.EnvironmentNodeRightMargin)); // -33 to account for show more button (considering 3 digits) to be removed from column width; +10 to account for tile margin
        }
        else {
            return actualEnvCount;
        }
    }

    private _instanceIdFormat = "Active-Release-{0}";
    private _isEnvironmentColumnResizing: boolean = false;
    private _isNameColumnResizing: boolean = false;
    private _activeReleasesTable: ITable<Release>;
    private _activeReleasesStore: ActiveReleasesStore;
    private _activeReleasesActions: ActiveReleasesActions;
    private _menuCommands: ActiveReleasesMenuCommands;
    private _columns: ITableColumn<Release>[] = [];
}
